<template>
  <div class="detail-content-block">
    <div class="detail">
      <quick-nav class="detail__navigation" :blocks="detail_blocks" />
      <div v-for="(block,key) in detail_blocks" v-bind:key="_uid+key">
        <component
          v-if="block.show"
          :key="_uid+block+key"
          :is="block.key"
          :details="details"
          :availabilities="availabilities"
          :availability="availability"
        />
      </div>
      <div class="detail__footer">
        <vb-button
          v-if="cta_status.disabled || !get_availability(selected_solution.pricing.live_price_no_tax).can_go_to_checkout"
          :vb_options="{
            text: 'Continua',
            variant: 'info',
            icon_disabled: 'lock',
            disabled: true
          }"
        />
        <div v-else>
          <card-recap v-if="!is_mobile()" class="detail__recap" :solution="selected_solution" />
          <custom-link
            :route_name="'checkout'" :wrap="'a'"
            :before_navigation="() => {
              this.$store.commit('livebkv3/hotel_details', details)
              // resetto product_quantities in caso di back navigation
              this.$store.commit('livebkv3/update_rooms_selection', { product_quantities: {} })
            }"
          >
            <template #content>
              <vb-button v-if="!is_mobile()" :vb_options="{text: 'Continua', variant: 'info'}" />
              <smart-recap v-else :products="sorting_products" :price="selected_solution.pricing.live_price_no_tax" />
            </template>
          </custom-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { sum, add, subtract } from 'mathjs'
import { deep_clone } from "@src/utilities"
import QuickNav from "@bookingv3/components/blocks/detail/quick-nav"
import Heading from "@bookingv3/components/blocks/detail/heading"
import Carousel from "@bookingv3/components/blocks/detail/carousel"
import Description from "@bookingv3/components/blocks/detail/description"
import Rooms from "@bookingv3/components/blocks/detail/rooms"
import Facilities from "@bookingv3/components/blocks/detail/facilities"
import Policies from "@bookingv3/components/blocks/detail/policies"
import Info from "@bookingv3/components/blocks/detail/info"
import InfoDsa from "@bookingv3/components/blocks/detail/info-dsa"
import CardRecap from "@bookingv3/components/blocks/card-recap"
import SmartRecap from "@bookingv3/components/blocks/smart-recap"

export default {
  name: "detail-content-block",

  components: {
    'quick-nav': QuickNav,
    'heading': Heading,
    'carousel': Carousel,
    'description': Description,
    'rooms': Rooms,
    'facilities': Facilities,
    'policies': Policies,
    'info': Info,
    'info-dsa': InfoDsa,
    'card-recap': CardRecap,
    'smart-recap': SmartRecap
  },

  props: {
    details: Object,
    availabilities: Object,
    availability: String,
  },

  data() {
    return {
      detail_blocks: [],
      cta_status: { disabled: true, label: '', variant: '' },
    }
  },

  provide() {
    return {
      cta_status_checker: this.set_cta_status
    }
  },

  computed: {
    ...mapState({
      // stato del modulo livebkv3
      selected_solution: state => state.livebkv3.selected_solution,
    }),

    // Ordinamento crescente delle camere occupancy
    sorting_occupancy() {
      let occupancy = [...this.selected_solution.occupancy],
          parsed_occupancy = []

      occupancy.forEach((occ, index) => {
        parsed_occupancy.push({
          ...occ,
          index: index
        })
      })

      // ordina le camere in modo crescente sul totale di adulti e bambini
      this.sort_rooms(parsed_occupancy)
      return parsed_occupancy
    },

    // Ordinamento crescente dei prodotti/camere selezionate
    sorting_products() {
      let rooms = [...this.selected_solution.rooms],
          parsed_products = []

      // copia maximum_occupancy di ogni prodotto scelto e lo pusha in occupancy_sorted considerando anche la quantità
      if(Object.keys(rooms).length > 0){
        rooms.forEach(room => {
          Object.values(room.products).forEach(product => {
            const qty = product.price_selected.qty
            for (let i = 0; i < qty; i++) {
              const maximum_occupancy = deep_clone(product.maximum_occupancy)
              parsed_products.push(maximum_occupancy)
              maximum_occupancy.id = product.id
            }
          })
        })
      }
      // ordina le camere in modo crescente sul totale di adulti e bambini
      this.sort_rooms(parsed_products)
      return parsed_products
    }
  },

  watch: {
    'selected_solution.rooms': {
      handler() {
        this.match_rooms()
      },
      deep: true
    }
  },

  created() {
  },

  mounted() {
    this.detail_blocks = [
      {
        key: "heading",
        show: true,
        quick_text: 'Panoramica'
      },
      {
        key: "carousel",
        show: true,
        quick_text: 'Gallery'
      },
      {
        key: "description",
        show: true,
        quick_text: 'Descrizione'
      },
      {
        key: "rooms",
        show: true,
        quick_text: 'Camere e prezzi'
      },
      {
        key: "facilities",
        show: true,
        quick_text: 'Servizi'
      },
      {
        key: "policies",
        show: true,
        quick_text: 'Leggi prima di prenotare'
      },
      {
        key: "info",
        show: true,
        quick_text: 'Da sapere'
      },
      {
        key: "info-dsa",
        show: true,
        quick_text: 'Informazioni legali'
      },
    ]
  },

  methods: {

    // Stato della cta
    set_cta_status(result) {
      this.cta_status = result
    },

    // Ordinamento camere
    sort_rooms(items) {

      const calculate_total = (el) => {
        const adults = el.adults || 0,
              children = el.children ? el.children.reduce((acc, child) => acc + (child.total || child), 0) : 0
        return sum(adults, children)
      }

      return items.sort((a,b) => {
        const total_a = calculate_total(a),
              total_b = calculate_total(b)
        return subtract(total_a, total_b)
      })
    },

    // Tipologia di matching(copy e variante colore)
    get_match_type(match_found) {

      const match_config = {
        success: {
          text: 'OK!',
          icon: 'check_circle',
          icon_color: '0c5028',
          variant: 'success'
        },
        warning: {
          text: 'Spazio nella camera insufficiente',
          icon: 'exclamation',
          icon_color: '90881e',
          variant: 'warning'
        },
        danger: {
          text: 'Camera non adatta',
          icon: 'exclamation',
          icon_color: '7b151c',
          variant: 'danger'
        }
      }

      return match_config[match_found]
    },

    // Matching bambini
    is_match_children(occ_children, prod_children, occ_adults, prod_adults) {

      if (!prod_children || prod_children.length === 0) {
        this.log(`il prodotto non ha children, impossibile abbinare bambini.`)
        prod_children = []
      }

      const available_children = prod_children.map(child => ({ ...child }))
      let adults_needed = occ_adults,
          fallback_children = 0

      for (const occ_child_age of occ_children) {
        let match_found = 'danger'

        for (const child_group of available_children) {
          this.log(`verifica età bambino: ${occ_child_age} con :`, child_group)
          if (occ_child_age >= child_group.from_age && occ_child_age <= child_group.to_age && child_group.total > 0) {
            child_group.total = subtract(child_group.total, 1)
            match_found = 'success'
            this.log(`corrispondenza trovata per età ${occ_child_age} in `, child_group)
            break
          }
        }
        if (match_found === 'danger') {
          if (sum(adults_needed, fallback_children) < prod_adults) {
            fallback_children = add(fallback_children, 1)
            this.log(`età ${occ_child_age} trattata come adulto pagante.`)
          } else {
            this.log(`nessuna corrispondenza trovata per età ${occ_child_age} e non può essere trattato come adulto.`)
            return false
          }
        }
      }

      const total_people_in_room = sum(adults_needed,fallback_children)
      if (total_people_in_room > prod_adults) {
        this.log(`numero totale di persone eccede la capacità della camera.`)
        return false
      }

      return true
    },

    // Confronta le camere e crea l'accommodation products
    match_rooms() {
      const occupancy_rooms = this.sorting_occupancy,
            products_rooms = this.sorting_products

      let result = []

      // il confronto avviene solo se entrambi gli array hanno lo stesso numero di camere
      if (occupancy_rooms.length === products_rooms.length) {
        const unmatched_products = [...products_rooms]

        for (const occ_room of occupancy_rooms) {
          let match_found = 'danger',
              matched_product_id

          for (let i = 0; i < unmatched_products.length; i++) {
            const prod_room = unmatched_products[i]

            // aggiorna sempre `matched_product_id` con l'id dell'ultimo prodotto confrontato
            matched_product_id = prod_room.id

            // verifica se la camera può ospitare almeno il numero di adulti, altrimenti è inutile confrontare i bambini
            if (prod_room.adults < occ_room.adults) continue

            // verifica compatibilità bambini, o assenza degli stessi
            const children_check =
                  (!occ_room.children.length && !prod_room.children)
                  || this.is_match_children(occ_room.children, prod_room.children, occ_room.adults, prod_room.adults)

            if (children_check) {
              unmatched_products.splice(i, 1)
              match_found = 'success'
              break
            }

            // gestione del caso warning
            if(!children_check && occ_room.adults <= occ_room.adults) {
              unmatched_products.splice(i, 1)
              match_found = 'warning'
            }
          }

          // aggiunge il risultato con chiave `matched` e dettagli sulla corrispondenza
          result.push({
            id: matched_product_id,
            allocation: {
              number_of_adults: occ_room.adults,
              children: occ_room.children,
              index: occ_room.index
            },
            matched: match_found,
            type: this.get_match_type(match_found)
          })
        }
      }

      this.log(result)

      this.$store.commit('livebkv3/update_accommodation_products', result)
      this.$store.commit('livebkv3/set_multiroom_assignment',
        result.length
        ? result.every(room => room.matched === 'success' || room.matched === 'warning')
        : false
      )
    },

  },
}
</script>

<style lang="scss" scoped>

  .detail {

    &__navigation {
      margin-bottom: $grid-gutter-width*2;
      display: none;

      @include media-breakpoint-up(xl){
        display: flex;
      }
    }

    &__recap {
      margin-bottom: $grid-gutter-width*2;
    }

    &__footer {
      @extend %section;
      @extend %section_margin;
      text-align: center;
      margin-bottom: $grid-gutter-width*3;

      @include media-breakpoint-up(md){
        text-align: right;
        margin-bottom: inherit;
      }

      .btn {
        width: 200px;
      }

      ::v-deep {
        .custom-link > * {
          display: block;
        }
      }
    }
  }

</style>